import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import QRCode from 'react-qr-code';
import { Flex, Heading, Image, Spinner, Stack, Text } from "@chakra-ui/react";
import { format, addSeconds, isPast } from 'date-fns';
import pt from 'date-fns/locale/pt';

import api from "../../services/api";
import { useToast } from '../../hooks/toast';
import { AppError } from '../../utils/AppError';
import Loading from "../../components/Loading";
import Button from "../../components/Button";

import { AnimationContainer, Container } from "./styles";

type UseParamsProps = {
	company: string;
	bank: string;
	txid: string;
}

const Pix: React.FC = () => {
	const { company, bank, txid } = useParams<UseParamsProps>();
	const { addToast } = useToast();

	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingCopiPix, setIsLoadingCopiPix] = useState(false);

	const [pixData, setPixData] = useState<any>({} as any);

	const fetchDataPix = async () => {
		try {
			// const { data } = await api.post(`/bb/pix/indextxid`, { txid }, { headers: { company }} );
			const { data } = await api.get(`/${bank}/pix/${txid}`, { headers: { company } });

			if (data.calendario) {
				const { criacao, expiracao } = data.calendario;
				const auxData = data;

				if (criacao && expiracao) {
					auxData.calendario.criacao = format(new Date(criacao), "dd'/'MM'/'yyyy 'ás' HH:mm", { locale: pt });
					const newExpiracao = addSeconds(new Date(criacao), expiracao);
					// console.log('newExpiração ==> ', newExpiracao);
					auxData.calendario.expired = isPast(new Date(newExpiracao));
					auxData.calendario.expiracao = format(new Date(newExpiracao), "dd'/'MM'/'yyyy 'ás' HH:mm", { locale: pt });
				}
			}
			
			setPixData(data);

		} catch (error: any) {
			console.log('ERROR PIX ==> ', error);

			const isAppError = error instanceof AppError;

			const msg = isAppError
				? error.message 
				: error.response?.data?.message
					? error.response?.data?.message
					: 'Não foi possível criar/editar a conta.';

			addToast({
				type: 'error',
				title: 'Pix Nåo Encontrado!',
				description: msg,
				
			});

		} finally {
			setIsLoading(false);
		}
	};

	const copyPix = async () => {
		try {
			setIsLoadingCopiPix(true);
			await navigator.clipboard.writeText(pixData.pixCopiaECola || pixData.brcode);
			
			addToast({
				type: 'success',
				title: 'Código PIX',
				description: 'O código PIX foi copiado!',
			});
			
		} catch (err) {
			addToast({
				type: 'error',
				title: 'Código PIX',
				description: 'Erro ao copiar o código PIX!',
			});
		} finally {
			setIsLoadingCopiPix(false);
		}
	};

	useEffect(() => {
		const handleFetchDataPix = async () => {
			await fetchDataPix();
		}

		handleFetchDataPix();
	}, [txid]);

	return (
		<>
			{isLoading ? (
				<Loading />
			) : (
				<Container>
					<Flex width="600px">
						<AnimationContainer>
							{pixData.calendario ? (
								<Stack
									flex={1}
									width="100%"
									justify="center"
									align="center"
									borderWidth={4}
									borderColor={`${pixData.status === 'CONCLUIDA'
										? 'green.600'
										: pixData.status === 'ATIVA' && pixData.calendario.expired
											? 'red.500'
											: 'gray.300'
									}`}
									borderRadius={10}
									pt={10}
									pb={5}
									mb={5}
									px={4}
								>
									<Image
										src={pixData.url_logomarca
											? pixData.url_logomarca
											: "https://safetycloud.blob.core.windows.net/cashback-promocoes/placeholder-image.png"
										}
										aspectRatio={4 / 3}
										fit={{ base: "contain", md: "contain", lg: "cover" }}
										width={"150px"}
										alt="Logo Empresa"
									/>

									<Heading fontSize={20} pb={5} pt={3}>Pagamento via Pix</Heading>

									<Stack width="100%">
										{(pixData.devedor && pixData.devedor.nome) && (
											<Stack flexDir="row">
												<Text fontWeight={600}>Cliente:</Text>
												<Text fontWeight={600}>{pixData.devedor.nome}</Text>
											</Stack>
										)}

										{pixData.devedor && (pixData.devedor.cpf || pixData.devedor.cnpj) && (
											<Stack flexDir="row">
												<Text fontWeight={600}>Doc.:</Text>
												<Text fontWeight={600}>{pixData.devedor.cpf ? pixData.devedor.cpf : pixData.devedor.cnpj}</Text>
											</Stack>
										)}

										{pixData.valor && pixData.valor.original && (
											<Stack flexDir="row">
												<Text fontWeight={600}>Valor:</Text>
												<Text fontWeight={600}>{pixData.valor.original}</Text>
											</Stack>
										)}

										{pixData.calendario && pixData.calendario.criacao && (
											<Stack flexDir="row">
												<Text fontWeight={600}>Data da geração:</Text>
												<Text fontWeight={500}>{pixData.calendario.criacao}</Text>
											</Stack>
										)}

										{pixData.calendario && pixData.calendario.expiracao && (
											<Stack flexDir="row">
												<Text fontWeight={600}>Expira em:</Text>
												<Text fontWeight={500}>{pixData.calendario.expiracao}</Text>
											</Stack>
										)}
									</Stack>

									{pixData.status === 'CONCLUIDA' && (
										<Heading py={4} color="green.600">Pix Pago!</Heading>
									)}

									{(pixData.calendario.expired && pixData.status === 'ATIVA') && (
										<Heading py={4} color="red.500">Pix Expirado!</Heading>
									)}

									{((pixData.pixCopiaECola && pixData.status === 'ATIVA' && !pixData.calendario.expired) 
										|| (pixData.brcode && pixData.status === 'ATIVA' && !pixData.calendario.expired)) 
										&& (
										<>
											<QRCode
												size={256}
												style={{
													height: "auto", maxWidth: "50%", width: "50%", borderWidth: 4, borderColor: "black",
													padding: 8, borderRadius: 8, margin: 12
												}}
												value={pixData.pixCopiaECola || pixData.brcode}
												viewBox={`0 0 255 255`}
											/>

											<Text fontSize={14} fontWeight={500} wordBreak="break-word">{pixData.pixCopiaECola || pixData.brcode}</Text>

											<Button type="submit" disabled={isLoadingCopiPix} onClick={copyPix}>
												Copiar código PIX
												{isLoadingCopiPix && (
													<Spinner color="white" />
												)}
											</Button>
										</>
									)}

									<Stack flexDir="row" pt={6}>
										<Image
											src={'https://safetycloud.blob.core.windows.net/logo-cliente/raziodigital.png'}
											fit={{ base: "contain", md: "contain", lg: "cover" }}
											width={"200px"}
											alt="Logo Empresa"
										/>
									</Stack>
								</Stack>
							) : (
								<Stack
									flex={1}
									width="100%"
									justify="center"
									align="center"
									borderWidth={4}
									borderColor={`
									${pixData.status === 'CONCLUIDA'
										? 'green.600'
										: pixData.status === 'ATIVA' && pixData.calendario.expired
											? 'red.500'
											: 'gray.300'
									}`}
									borderRadius={10}
									pt={10}
									pb={5}
									mb={5}
									px={4}
								>
									<Heading>Pix não encontrado!</Heading>
								</Stack>
							)}
						</AnimationContainer>
					</Flex>
				</Container>
			)}
		</>
	);
};

export default Pix;